// Phase 3.1c — Product History drawer.
//
// Replaces the antd <Drawer> + MUI <TreeView>/<TreeItem> history view with a
// shadcn <Sheet> + a grouped <Accordion>. Presentation only: the parent still
// owns `productHistoryData`/`historyData` and the size/color reference lists, so
// data fetching (getProductHistoryAction) and shaping are unchanged.
import * as React from "react";
import moment from "moment";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "ui/sheet";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "ui/accordion";
import { EmptyState } from "../../shared/empty-state";

export interface ProductHistorySheetProps {
  open: boolean;
  onClose: () => void;
  /** The product row the history belongs to (productId, productName, mrp, sizeRefId, colorRefId). */
  historyData: any;
  /** History map: { [dateKey]: Array<{ updatedOn, status }> } — legacy shape, unchanged. */
  productHistoryData: any;
  /** Object.keys(productHistoryData) computed in the parent. */
  historyDates: string[];
  sizeList: any[];
  colorList: any[];
}

export function ProductHistorySheet({
  open,
  onClose,
  historyData,
  productHistoryData,
  historyDates,
  sizeList,
  colorList,
}: ProductHistorySheetProps) {
  // Resolve size/color names from the reference lists (legacy did the same via filter/map).
  const size = (sizeList || []).find((x: any) => x.id == historyData?.sizeRefId)?.name;
  const color = (colorList || []).find((x: any) => x.id == historyData?.colorRefId)?.name;

  // Flatten { date: entries[] } into a single ordered list of history entries so
  // each one becomes its own collapsible row (the legacy TreeItem structure).
  const entries: any[] = [];
  (historyDates || []).forEach((key: string) => {
    (productHistoryData?.[key] || []).forEach((entry: any) => entries.push(entry));
  });

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>History</SheetTitle>
        </SheetHeader>

        <div className="mt-4 mz">
          <div className="flex items-start justify-between gap-4 rounded-xl border p-4">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-foreground">
                {historyData?.productId} - {historyData?.productName}
              </div>
              <div className="mt-1 flex gap-4 text-xs text-muted-foreground">
                <span>Color - {color}</span>
                <span>Size - {size}</span>
              </div>
            </div>
            <div className="shrink-0 text-sm font-medium tabular-nums text-foreground">
              {historyData?.mrp}/-
            </div>
          </div>

          {entries.length !== 0 ? (
            <Accordion type="multiple" defaultValue={["h-0"]} className="mt-4">
              {entries.map((entry: any, index: number) => (
                <AccordionItem key={index} value={`h-${index}`}>
                  <AccordionTrigger>
                    {moment(entry?.updatedOn).format("DD,MMM - YYYY")}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="text-sm text-muted-foreground">{entry?.status}</div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <EmptyState className="mt-4" />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ProductHistorySheet;
